import { useMemo } from "react";
import { ShieldCheck, ShieldAlert } from "lucide-react";

/**
 * Password strength meter — Phase 6.5.
 *
 * Pure component, no external dependency. Calculates a 0–4 score from
 * length + character-class diversity + a small dictionary-style penalty.
 * Used on the reset-password and change-password screens.
 */
export function evaluatePassword(pw) {
  const value = String(pw || "");
  if (!value) return { score: 0, label_vi: "Trống", labelColor: "text-espresso-muted", barClass: "bg-espresso/10", ok: false };

  let score = 0;
  if (value.length >= 8) score += 1;
  if (value.length >= 12) score += 1;
  if (/[A-Z]/.test(value) && /[a-z]/.test(value)) score += 1;
  if (/\d/.test(value)) score += 1;
  if (/[^A-Za-z0-9]/.test(value)) score += 1;

  // Penalize obvious patterns
  const lower = value.toLowerCase();
  const tooCommon = ["password", "12345678", "qwerty", "nailviet", "admin", "abc123"].some((p) => lower.includes(p));
  if (tooCommon) score = Math.max(0, score - 2);
  if (/^(.)\1+$/.test(value)) score = 0;

  // Clamp to 0..4
  score = Math.max(0, Math.min(4, score));

  if (value.length < 8) {
    return { score: 0, label_vi: "Quá ngắn", labelColor: "text-red-700", barClass: "bg-red-400", ok: false };
  }

  const levels = [
    { label_vi: "Rất yếu",   labelColor: "text-red-700",      barClass: "bg-red-400" },
    { label_vi: "Yếu",       labelColor: "text-orange-700",   barClass: "bg-orange-400" },
    { label_vi: "Trung bình", labelColor: "text-amber-700",    barClass: "bg-amber-400" },
    { label_vi: "Khá mạnh",  labelColor: "text-emerald-700",  barClass: "bg-emerald-500" },
    { label_vi: "Rất mạnh",  labelColor: "text-emerald-800",  barClass: "bg-emerald-600" },
  ];
  const cfg = levels[score];
  return { score, label_vi: cfg.label_vi, labelColor: cfg.labelColor, barClass: cfg.barClass, ok: score >= 2 };
}

export default function PasswordStrengthMeter({ password, testIdPrefix = "pw-strength" }) {
  const result = useMemo(() => evaluatePassword(password), [password]);
  const segments = [0, 1, 2, 3, 4];
  return (
    <div data-testid={`${testIdPrefix}-wrap`} className="mt-2">
      <div className="grid grid-cols-5 gap-1.5" data-testid={`${testIdPrefix}-bars`}>
        {segments.map((s) => (
          <div
            key={s}
            data-testid={`${testIdPrefix}-bar-${s}`}
            className={`h-1.5 rounded-full transition-colors ${
              s <= result.score - 1 ? result.barClass : "bg-espresso/10"
            }`}
          />
        ))}
      </div>
      <div className="mt-1.5 flex items-center justify-between text-[11px] tracking-[0.18em] uppercase font-semibold">
        <span className={result.labelColor} data-testid={`${testIdPrefix}-label`}>
          {result.ok ? <ShieldCheck className="inline w-3 h-3 mr-1 -mt-0.5" /> : <ShieldAlert className="inline w-3 h-3 mr-1 -mt-0.5" />}
          Độ mạnh: {result.label_vi}
        </span>
        <span className="text-espresso-muted">
          Tối thiểu 8 ký tự
        </span>
      </div>
    </div>
  );
}
